import { ArrowLeftRight } from "lucide-react";
import ColorPicker from "./ColorPicker";
import { DEFAULT_STROKE } from "./templates";
import {
  BRUSHES,
  DASHES,
  HEADS,
  HIGHLIGHTER_ALPHA,
  ROUTES,
  SHAPES,
  arrowGeometry,
  brushOutline,
  dashArray,
  shapePoints,
  smooth,
} from "./vector";

const QUICK_COLOURS = ["#a8563c", "#1e1b16", "#ffffff", "#2f6fd6", "#3f6742", "#f2c94c"];
const HIGHLIGHTER = BRUSHES.find((brush) => brush.key === "highlighter");
const WAVE = smooth([[3, 13], [8, 6], [14, 11], [20, 5], [25, 9]]);
const pointList = (points) => points.map((point) => point.join(",")).join(" ");

// --- little previews, drawn with the same geometry as the board ------------------

function Preview({ children }) {
  return (
    <svg viewBox="0 0 28 18" width="28" height="18" aria-hidden="true">
      {children}
    </svg>
  );
}

function BrushPreview({ brush }) {
  if (brush === "brush") return <Preview><polygon points={pointList(brushOutline(WAVE, 3.4))} fill="currentColor" /></Preview>;
  const width = { pencil: 1.1, marker: 2.8, highlighter: 5 }[brush];
  return (
    <Preview>
      <polyline
        points={pointList(WAVE)}
        fill="none"
        stroke={brush === "highlighter" ? "#d9a800" : "currentColor"}
        strokeOpacity={brush === "highlighter" ? HIGHLIGHTER_ALPHA + 0.2 : 1}
        strokeWidth={width}
        strokeLinecap={brush === "highlighter" ? "butt" : "round"}
        strokeLinejoin="round"
      />
    </Preview>
  );
}

function ShapePreview({ shape }) {
  return (
    <Preview>
      <polygon points={pointList(shapePoints(shape, 6, 2, 16, 14))} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </Preview>
  );
}

function ArrowPreview({ start = "none", end = "arrow", route = "straight", mirror = false }) {
  const from = route === "straight" && !mirror ? [4, 9] : [4, 14];
  const to = route === "straight" && !mirror ? [24, 9] : [24, 4];
  const { path, parts } = arrowGeometry(mirror ? [4, 9] : from, mirror ? [24, 9] : to, 1.3, { route, bend: 0.5, start, end });
  return (
    <Preview>
      <g transform={mirror ? "translate(28 0) scale(-1 1)" : undefined}>
        <polyline points={pointList(path)} fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        {parts.map((part, index) =>
          part.kind === "fill" ? (
            <polygon key={index} points={pointList(part.points)} fill="currentColor" />
          ) : part.kind === "circle" ? (
            <circle key={index} cx={part.center[0]} cy={part.center[1]} r={part.r} fill="currentColor" />
          ) : (
            <polyline key={index} points={pointList(part.points)} fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          )
        )}
      </g>
    </Preview>
  );
}

function DashPreview({ dash }) {
  return (
    <Preview>
      <line x1="4" y1="9" x2="24" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeDasharray={dashArray(dash, 1.8)} />
    </Preview>
  );
}

// --- controls ----------------------------------------------------------------------

function Group({ label, layout, children }) {
  return layout === "bar" ? (
    <div className="mb-opt-group" role="group" aria-label={label}>
      {children}
    </div>
  ) : (
    <div className="mb-field">
      <span className="mb-field-label">{label}</span>
      {children}
    </div>
  );
}

function Choices({ label, options, value, onChange, render, layout }) {
  return (
    <Group label={label} layout={layout}>
      <div className="mb-choices" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={value === option.key}
            aria-label={option.label}
            title={option.label}
            className={value === option.key ? "active" : ""}
            onClick={() => onChange(option.key)}
          >
            {render(option.key)}
          </button>
        ))}
      </div>
    </Group>
  );
}

function Size({ label, value, min, max, onChange, layout }) {
  return (
    <Group label={layout === "bar" ? label : `${label} · ${value ? value.toFixed(2) : "none"}`} layout={layout}>
      <input
        className="mb-opt-range"
        type="range"
        min={min}
        max={max}
        step={0.05}
        value={value}
        aria-label={label}
        title={`${label}: ${value ? value.toFixed(2) : "none"}`}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </Group>
  );
}

function Colour({ label, value, onChange, layout, palettes }) {
  if (layout !== "bar") {
    return (
      <Group label={label} layout={layout}>
        <ColorPicker label={label} value={value} palettes={palettes} onChange={onChange} />
      </Group>
    );
  }
  return (
    <Group label={label} layout={layout}>
      <div className="mb-quick-colours">
        {QUICK_COLOURS.map((colour) => (
          <button
            key={colour}
            type="button"
            className={colour === value ? "active" : ""}
            style={{ background: colour }}
            onClick={() => onChange(colour, false)}
            aria-label={`${label} ${colour}`}
            title={colour}
          />
        ))}
        <label className="mb-custom-colour" title="Any colour" style={{ background: QUICK_COLOURS.includes(value) ? undefined : value }}>
          <input type="color" value={value || "#000000"} onChange={(event) => onChange(event.target.value, true)} aria-label={`Custom ${label.toLowerCase()}`} />
        </label>
      </div>
    </Group>
  );
}

// kind is "pencil", "shape" or "arrow"; value is the tool style or the selected
// item. layout "bar" is the floating options bar, "panel" the right-hand panel.
export default function DrawOptions({ kind, value, onChange, layout = "panel", palettes = [], mergeKey = kind }) {
  const set = (patch) => onChange(patch);
  const slide = (key) => (next) => onChange({ [key]: next }, { merge: `${mergeKey}:${key}` });
  const colour = (key) => (next, continuous) => onChange({ [key]: next }, continuous ? { merge: `${mergeKey}:${key}` } : undefined);
  const dashes = (kind === "pencil" && value.brush === "highlighter" ? DASHES.filter((d) => d.key !== "dotted") : DASHES);

  const chooseBrush = (brush) => {
    const preset = BRUSHES.find((entry) => entry.key === brush);
    const patch = { brush, stroke_width: preset.stroke_width };
    // Highlighters are yellow by default; leaving one restores the usual ink.
    if (brush === "highlighter") patch.stroke = HIGHLIGHTER.stroke;
    else if (value.stroke === HIGHLIGHTER.stroke) patch.stroke = DEFAULT_STROKE;
    if (brush === "brush" || (brush === "highlighter" && value.dash === "dotted")) patch.dash = "solid";
    set(patch);
  };

  return (
    <div className={layout === "bar" ? "mb-options-bar" : "mb-options-panel"}>
      {kind === "pencil" && (
        <Choices label="Brush" layout={layout} options={BRUSHES} value={value.brush || "pencil"} onChange={chooseBrush} render={(key) => <BrushPreview brush={key} />} />
      )}
      {kind === "shape" && (
        <Choices label="Shape" layout={layout} options={SHAPES} value={value.shape || "rect"} onChange={(shape) => set({ shape })} render={(key) => <ShapePreview shape={key} />} />
      )}
      {kind === "arrow" && (
        <>
          <Choices label="Path" layout={layout} options={ROUTES} value={value.route || "straight"} onChange={(route) => set({ route })} render={(key) => <ArrowPreview route={key} />} />
          <Choices label="Start" layout={layout} options={HEADS} value={value.head_start || "none"} onChange={(head_start) => set({ head_start })} render={(key) => <ArrowPreview end={key} mirror />} />
          <Choices label="End" layout={layout} options={HEADS} value={value.head_end || "arrow"} onChange={(head_end) => set({ head_end })} render={(key) => <ArrowPreview end={key} />} />
        </>
      )}

      {!(kind === "pencil" && value.brush === "brush") && (
        <Choices label="Line" layout={layout} options={dashes} value={value.dash || "solid"} onChange={(dash) => set({ dash })} render={(key) => <DashPreview dash={key} />} />
      )}
      <Size
        label={kind === "shape" ? "Outline" : "Size"}
        layout={layout}
        value={value.stroke_width ?? 0.35}
        // A shape with no fill must keep some outline, or it would vanish.
        min={kind === "shape" && value.fill ? 0 : 0.1}
        max={kind === "pencil" ? 4 : 2.5}
        onChange={slide("stroke_width")}
      />
      <Colour label={kind === "shape" ? "Outline colour" : "Colour"} layout={layout} value={value.stroke || DEFAULT_STROKE} onChange={colour("stroke")} palettes={palettes} />

      {kind === "shape" && (
        <>
          <Group label="Fill" layout={layout}>
            <label className="mb-toggle">
              <input
                type="checkbox"
                checked={Boolean(value.fill)}
                onChange={(event) =>
                  set(
                    event.target.checked
                      ? { fill: value.fill || "#f1e4d3" }
                      : { fill: null, ...(value.stroke_width ? {} : { stroke_width: 0.35 }) }
                  )
                }
              />
              <span>{layout === "bar" ? "Fill" : "Fill the shape"}</span>
            </label>
          </Group>
          {value.fill && <Colour label="Fill colour" layout={layout} value={value.fill} onChange={colour("fill")} palettes={palettes} />}
          {(value.shape || "rect") === "rect" && (
            <Group label={layout === "bar" ? "Corners" : `Corner radius · ${value.radius || 0}%`} layout={layout}>
              <input
                className="mb-opt-range"
                type="range"
                min={0}
                max={50}
                step={1}
                value={value.radius || 0}
                aria-label="Corner radius"
                onChange={(event) => onChange({ radius: Number(event.target.value) }, { merge: `${mergeKey}:radius` })}
              />
            </Group>
          )}
        </>
      )}

      {kind === "arrow" && layout !== "bar" && (
        <button
          type="button"
          className="mb-wide-button"
          onClick={() => set({ head_start: value.head_end || "arrow", head_end: value.head_start || "none" })}
        >
          <ArrowLeftRight size={15} strokeWidth={1.8} aria-hidden="true" />
          Reverse direction
        </button>
      )}
    </div>
  );
}
